import * as yup from 'yup'
import type { InsurancePolicyProductFormMode } from './InsurancePolicyProductForm.interface'

/**
 * Blank means "not set", not zero. Without this transform an empty text input casts to `NaN`
 * and every optional money field would fail `typeError` the moment it is left alone.
 */
const blankToUndefined = (value: unknown, originalValue: unknown) => {
  if (typeof originalValue === 'string' && originalValue.trim() === '') {
    return undefined
  }

  return value
}

// The backend's `@Max` on each fee: the most its `int` / `numeric(4,2)` column can store.
const MAX_FEE_CENTS = 2147483647
const MAX_FEE_PERCENT = 99.99

/** `feeCents` and `feeCapCents` are `@IsInt @Min(0) @Max(MAX_FEE_CENTS)` on both backend DTOs. */
const optionalCents = yup
  .number()
  .transform(blankToUndefined)
  .typeError('Enter a whole number of cents')
  .integer('Enter a whole number of cents')
  .min(0, 'Cannot be negative')
  .max(MAX_FEE_CENTS, `Cannot be more than ${MAX_FEE_CENTS.toLocaleString('en-US')} cents`)

/** `feePercent` is `@IsNumber @Min(0) @Max(MAX_FEE_PERCENT)` — fractions are legitimate. */
const optionalPercent = yup
  .number()
  .transform(blankToUndefined)
  .typeError('Enter a number')
  .min(0, 'Cannot be negative')
  .max(MAX_FEE_PERCENT, `Cannot be more than ${MAX_FEE_PERCENT}%`)

/**
 * The three create-only fields are required on create and NOT required on edit: each is either
 * locked or hidden in edit mode, and `AdminInsurancePolicyProductUpdateDto` rejects all three
 * outright — so requiring them would block a legitimate save on a value the form cannot send.
 */
export const buildInsurancePolicyProductValidator = (mode: InsurancePolicyProductFormMode) => {
  const isCreate = mode === 'create'

  return yup.object({
    insuranceProductId: isCreate
      ? yup.string().trim().required('Select an insurance product')
      : yup.string().trim(),
    feeFrequency: isCreate
      ? yup.string().trim().required('Select a charge frequency')
      : yup.string().trim(),
    approvalType: isCreate
      ? yup.string().trim().required('Select an approval type')
      : yup.string().trim(),
    feeCents: optionalCents,
    feeCapCents: optionalCents,
    feePercent: optionalPercent,
    // `@IsUrl` on both DTOs, so a malformed value is a 400. Blank is allowed here and omitted
    // entirely by the mapper — an empty string would fail that validator server-side.
    coverageDetailsPdfUrl: yup.string().trim().url('Enter a valid URL, including https://'),
    description: yup.string().trim(),
    insuranceRequirements: yup.string().trim(),
    coverage: yup.string().trim(),
    coverageDetails: yup.string().trim(),
    documentUploadTitleWhenOptedIn: yup.string().trim(),
    insuranceDocusignTemplateId: yup.string().trim(),
    manualUploadRequirement1: yup.string().trim(),
    manualUploadRequirement2: yup.string().trim(),
    manualUploadRequirement3: yup.string().trim(),
    manualUploadRequirement4: yup.string().trim(),
    allowOptOut: yup.boolean(),
    requireExpirationDate: yup.boolean(),
    requireDocumentUploadWhenOptedIn: yup.boolean(),
    lockCollectedDocuments: yup.boolean(),
  })
}
